import { supabase } from './supabase';
import { normalizarTexto, columnaNormalizada } from '../utils/normalizacionBusqueda';
import { uploadInventoryImage, deleteInventoryImage, inventoryImageOpts } from './inventoryService';
// #568 — `tools.image_url` guarda la RUTA del objeto en storage, nunca una URL:
// una URL firmada es una credencial con vencimiento y la foto se muere sola.
// Se firma al leer. Ver la cabecera de `src/utils/toolImageStorage.js`.
import {
  INVENTORY_IMAGES_BUCKET,
  canonicalImageValue,
  resolveRowsImageField,
  resolveImageValue
} from '../utils/toolImageStorage';
import { createNotification } from './notificationService';
import { callMcpTool } from './mcpClient';
// #567 — gemelo de `mcp-server/src/utils/toolSearch.js`. Es lo que pone en
// paridad el buscador de la GUI con el del agente; ver la cabecera de ese
// archivo y `mcp-server/tests/tools/toolSearchLaneParity.test.js`.
import { searchToolRows } from '../utils/toolSearch';

/**
 * Agrega una línea a `notes` en vez de pisarla.
 *
 * `tools.notes` son las notas INTERNAS del equipo (dónde se calibró, qué
 * repuesto lleva, con qué no mezclar). `updateToolCondition`/`retireTool`
 * escribían ahí el motivo de la baja con un `notes = reason` pelado, o sea
 * que marcar una herramienta como perdida borraba todo lo que se sabía de
 * ella justo cuando más se necesita el historial.
 */
const appendNote = (existing, line) =>
  [existing, line].filter((s) => s && String(s).trim()).join('\n');

const todayStamp = () => new Date().toISOString().slice(0, 10);

/**
 * #567 — el buscador de herramientas de la GUI, en paridad con el del agente.
 *
 * Hasta este issue esta función buscaba así:
 *
 *     if (search) q = q.ilike('name', `%${search}%`);
 *
 * o sea: UN solo campo (`name`), substring CONTIGUO, y sensible a acentos
 * porque el `ILIKE` de Postgres no normaliza. Medido contra el fixture local,
 * quien escribía **"taladro rojo"** obtenía **0 filas** — el nombre real es
 * "Taladro percutor rojo Milwaukee M18 FUEL" y no contiene ese substring
 * contiguo — mientras `list_tools` (el carril del agente, arreglado en #565)
 * ya devolvía las DOS herramientas rojas. La GUI de Jorge era más angosta que
 * el agente incluso ANTES del fix del agente.
 *
 * Ahora las dos corren `searchToolRows` de `utils/toolSearch.js`, que es gemelo
 * byte a byte del de `mcp-server/src/utils/`. Ver la cabecera de ese archivo.
 *
 * PAGINACIÓN — la diferencia real entre los dos carriles y por qué se resuelve
 * así: el filtro es en JS (Postgres no sabe de acentos ni de concordancia de
 * género), así que la base **no puede** paginarlo ni contarlo. Con `search`
 * activo se traen las filas que pasan los filtros que sí son de la DB (status /
 * categoría), se filtra en JS, y **el `count` se toma del resultado filtrado
 * ANTES de cortar la página** — el `count: 'exact'` de PostgREST contaría el
 * total SIN filtrar y mentiría por exceso. Sin `search` no cambia nada: pagina
 * y cuenta la base, como siempre.
 *
 * El catálogo de `tools` es chico (~15-100 filas; producción tenía 39 al
 * escribir esto), que es la misma razón por la que el carril MCP filtra en JS.
 * Si algún día no lo fuera, lo que corresponde es la extensión `unaccent` con
 * índice — no volver al `ilike` de un solo campo.
 *
 * @returns {{data, count, error, search_mode, search_terms}} `search_mode` es
 *   'exact' (todos los términos calzaron — incluido el cero honesto), 'partial'
 *   (el AND estricto dio cero y esto son candidatos rankeados, que NO son la
 *   respuesta) o null (no se buscó nada). Cada fila en modo 'partial' trae
 *   `_matchType` / `_matchedTerms` / `_unmatchedTerms`.
 */
export const listTools = async ({ search, status, category, category_id, limit = 50, offset = 0 } = {}) => {
  try {
    let q = supabase
      .from('tools')
      .select(`
        *,
        category_rel:tool_categories(id, slug, label_es, icon),
        location_rel:tool_locations(id, code, label, type),
        active_assignments:tool_assignments(id, holder_id, assigned_at, returned_at, holder:profiles!holder_id(id, full_name))
      `, { count: 'exact' })
      .order('name');
    // Sin búsqueda paginamos en la base. Con búsqueda NO: el filtro es en JS y
    // un `.range()` acá cortaría ANTES de filtrar (ver el bloque de arriba).
    if (!search) q = q.range(offset, offset + limit - 1);
    if (status) q = q.eq('status', status);
    if (category_id) q = q.eq('category_id', category_id);
  // #737/#742 — igualdad NORMALIZADA, no exacta: el dato esta guardado sin
  // tildes y `category = 'electrico'` devolvia 0 de 15. Cambio de conducta
  // declarado en el issue: la categoria pasa a ser una clase de equivalencia.
    else if (category) q = q.eq(columnaNormalizada('category'), normalizarTexto(category));
    const { data, error, count } = await q;
    if (error) return { data, count, error };

    let rows = data || [];
    let searchMode = null;
    let searchTerms = [];
    let effectiveCount = count;

    if (search) {
      const result = searchToolRows(rows, search);
      searchMode = result.searchMode;
      searchTerms = result.tokens;
      rows = result.tools;
      // El total es el del conjunto FILTRADO, y se lee antes del slice.
      effectiveCount = rows.length;
      if (result.partialMatches) {
        rows.forEach(t => {
          const m = result.partialMatches.get(t.id);
          if (m) {
            t._matchType = 'partial';
            t._matchedTerms = m.matched;
            t._unmatchedTerms = m.missing;
          }
        });
      } else if (searchMode === 'exact') {
        rows.forEach(t => { t._matchType = 'exact'; });
      }
      rows = rows.slice(offset, offset + limit);
    }

    // #568 — `image_url` viene de la base como RUTA; se firma acá, en UNA
    // tanda para toda la página (no una llamada por miniatura).
    await resolveRowsImageField(supabase, rows, await inventoryImageOpts());

    rows.forEach(t => {
      t._active_assignment = (t.active_assignments || []).find(a => !a.returned_at) || null;
      // Display helper: prefer the joined category, fall back to free-text
      t._categoryLabel = t.category_rel?.label_es || t.category || null;
      t._locationLabel = t.location_rel?.label || t.location || null;
    });

    return {
      data: data ? rows : data,
      count: effectiveCount,
      error,
      search_mode: searchMode,
      search_terms: searchTerms
    };
  } catch (error) {
    return { data: null, count: 0, error };
  }
};

export const getToolDetail = async (toolId) => {
  try {
    const { data, error } = await supabase
      .from('tools')
      .select(`
        *,
        category_rel:tool_categories(id, slug, label_es, icon),
        location_rel:tool_locations(id, code, label, type),
        active_assignments:tool_assignments(id, holder_id, assigned_at, returned_at, notes, project_id,
          holder:profiles!holder_id(id, full_name),
          project:projects(id, project_number, title)
        )
      `)
      .eq('id', toolId)
      .single();
    if (data) {
      // #568 — la ficha pinta `tool.image_url`, que en la base es una ruta.
      if (data.image_url) {
        data.image_url = await resolveImageValue(supabase, data.image_url, await inventoryImageOpts());
      }
      data._active_assignment = (data.active_assignments || []).find(a => !a.returned_at) || null;
      data._categoryLabel = data.category_rel?.label_es || data.category || null;
      // El detalle pintaba `tool.location` (columna de texto legada) mientras
      // el modal de edición sólo escribe `location_id`: cambiar la ubicación
      // se veía en el listado (que sí resuelve `location_rel`) y NO en el
      // detalle. Misma resolución que `listTools`, misma fuente de verdad.
      data._locationLabel = data.location_rel?.label || data.location || null;
    }
    return { data, error };
  } catch (error) {
    return { data: null, error };
  }
};

export const listMyAssignments = async ({ status = 'active', limit = 50, offset = 0 } = {}) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { data: [], error: null };

    let q = supabase
      .from('tool_assignments')
      .select(`
        *,
        tool:tools(id, serial_number, name, category, category_id, condition, image_url,
          category_rel:tool_categories(label_es, slug)
        ),
        project:projects(id, project_number, title)
      `)
      .eq('holder_id', user.id)
      .order('assigned_at', { ascending: false })
      .range(offset, offset + limit - 1);
    if (status === 'active') q = q.is('returned_at', null);
    else if (status === 'returned') q = q.not('returned_at', 'is', null);
    const { data, error } = await q;
    if (data) {
      // #568 — "Mis herramientas" pinta `a.tool.image_url` (ruta en la base).
      await resolveRowsImageField(supabase, data, {
        ...(await inventoryImageOpts()),
        field: 'tool.image_url'
      });
      data.forEach(a => {
        if (a.tool) {
          a.tool._categoryLabel = a.tool.category_rel?.label_es || a.tool.category || null;
        }
      });
    }
    return { data, error };
  } catch (error) {
    return { data: null, error };
  }
};

export const listActiveAssignments = async ({ project_id, holder_id, limit = 100, offset = 0 } = {}) => {
  try {
    let q = supabase
      .from('tool_assignments')
      .select(`
        *,
        tool:tools(id, serial_number, name, category),
        holder:profiles!holder_id(id, full_name),
        project:projects(id, project_number, title)
      `, { count: 'exact' })
      .is('returned_at', null)
      .order('assigned_at', { ascending: false })
      .range(offset, offset + limit - 1);
    if (project_id) q = q.eq('project_id', project_id);
    if (holder_id) q = q.eq('holder_id', holder_id);
    const { data, error, count } = await q;
    return { data, count, error };
  } catch (error) {
    return { data: null, count: 0, error };
  }
};

/**
 * Self-service pickup from warehouse. Per decision D7 (see wiki).
 * The handler forces holder_id = caller server-side; we don't send it.
 * Use this when the technician takes a tool for themselves.
 *
 * Calls the MCP endpoint instead of the Supabase client directly so the
 * server-side audit log + admin broadcast notification both fire.
 */
export const pickupTool = async ({ tool_id, project_id, notes }) =>
  callMcpTool('pickup_tool', {
    tool_id,
    project_id: project_id || null,
    notes: notes || null
  });

export const assignTool = async ({ tool_id, holder_id, project_id, notes }) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { data: null, error: new Error('Not authenticated') };

    const targetHolder = holder_id || user.id;

    const { data, error } = await supabase
      .from('tool_assignments')
      .insert({
        tool_id,
        holder_id: targetHolder,
        assigned_by: user.id,
        project_id: project_id || null,
        notes: notes || null
      })
      .select()
      .single();
    if (error) return { data: null, error };

    await supabase.from('tools').update({ status: 'assigned' }).eq('id', tool_id);
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

/**
 * Return a tool. Per D3 (#87): when return_condition !== 'good',
 * return_photo_url is REQUIRED. We route through the MCP endpoint so the
 * server-side validation, tool.condition update, and audit log fire.
 */
export const returnTool = async ({ assignment_id, tool_id, return_destination, notes, return_condition, return_photo_url }) => {
  try {
    // Prefer MCP path (handles audit, condition propagation, photo gate)
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return { data: null, error: new Error('Not authenticated') };

    // The MCP handler uses tool_id, not assignment_id.
    // Look it up from the assignment if only assignment_id was passed.
    let effectiveToolId = tool_id;
    if (!effectiveToolId && assignment_id) {
      const { data: a } = await supabase
        .from('tool_assignments')
        .select('tool_id')
        .eq('id', assignment_id)
        .single();
      effectiveToolId = a?.tool_id;
    }
    if (!effectiveToolId) return { data: null, error: new Error('Missing tool_id or assignment_id') };

    // El guard de mcpUrl se queda ACÁ, no dentro de callMcpTool: este es el
    // único call site del repo que, sin mcp-server configurado, no falla sino
    // que cae al write directo de abajo. callMcpTool devolvería un error y se
    // comería ese fallback.
    if (process.env.REACT_APP_MCP_SERVER_URL) {
      return callMcpTool('return_tool', {
        tool_id: effectiveToolId,
        notes: notes || null,
        return_condition: return_condition || null,
        return_photo_url: return_photo_url || null
      });
    }

    // Fallback: legacy direct supabase write (no MCP server configured)
    const { data, error } = await supabase
      .from('tool_assignments')
      .update({
        returned_at: new Date().toISOString(),
        return_destination: return_destination || 'warehouse',
        return_condition: return_condition || null,
        return_photo_url: return_photo_url || null,
        notes: notes || null
      })
      .eq('id', assignment_id)
      .select('tool_id')
      .single();
    if (error) return { data: null, error };
    if ((return_destination || 'warehouse') === 'warehouse') {
      await supabase.from('tools').update({ status: 'available' }).eq('id', data.tool_id);
    }
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const updateToolCondition = async ({ tool_id, condition, status, notes }) => {
  try {
    const updates = { condition };
    if (status) updates.status = status;
    if (notes !== undefined && notes !== null) {
      const { data: current } = await supabase
        .from('tools').select('notes').eq('id', tool_id).single();
      updates.notes = appendNote(current?.notes, `[${todayStamp()}] ${notes}`);
    }
    const { data, error } = await supabase
      .from('tools')
      .update(updates)
      .eq('id', tool_id)
      .select()
      .single();
    return { data, error };
  } catch (error) {
    return { data: null, error };
  }
};

/**
 * Marcar una herramienta como PERDIDA — las tres cosas que el diálogo promete.
 *
 * El diálogo destructivo lista como consecuencias "cambia la condición",
 * "cierra la asignación activa" y "notifica al administrador". Antes esto era
 * `updateToolCondition`, que hacía SOLO la primera: la herramienta seguía
 * apareciendo como "Asignada" en el listado, en "Asignaciones activas" y en
 * "Mis herramientas", y nadie se enteraba.
 *
 * Qué permite la base (verificado por SELECT sobre prod, 2026-07-28):
 *  - `tools_status_check` NO admite 'lost' como *status* — sólo
 *    available/assigned/maintenance/retired. La pérdida vive en `condition`
 *    (`tools_condition_check` sí admite 'lost'); el status que corresponde es
 *    'retired', que es además lo que dice el propio diálogo ("si aparece
 *    después, debe re-registrarse o reactivarse manualmente").
 *  - `tools_update` es `is_admin()`. Por eso el UPDATE de `tools` va PRIMERO:
 *    si el que llama no es admin, revienta ahí y no deja una asignación
 *    cerrada contra una herramienta que sigue diciendo "asignada".
 *  - `notifications` admite el INSERT para otro `user_id` sólo a admin/
 *    supervisor, y `tool_lost_reported` es un `notification_type` válido.
 *
 * La notificación es best-effort a propósito: que falle el aviso no puede
 * deshacer una pérdida ya registrada.
 */
export const reportToolLost = async ({ tool_id, reason }) => {
  try {
    if (!tool_id) return { data: null, error: new Error('Falta el identificador de la herramienta') };

    const { data: tool, error: readError } = await supabase
      .from('tools')
      .select('id, name, serial_number, notes')
      .eq('id', tool_id)
      .single();
    if (readError) return { data: null, error: readError };

    // 1) La herramienta. Primero, porque es el paso con RLS más estricta.
    const { data: updated, error: toolError } = await supabase
      .from('tools')
      .update({
        condition: 'lost',
        status: 'retired',
        notes: appendNote(tool.notes, `[PÉRDIDA ${todayStamp()}] ${reason || 'Sin detalle'}`)
      })
      .eq('id', tool_id)
      .select()
      .single();
    if (toolError) return { data: null, error: toolError };

    // 2) Cerrar TODA asignación abierta (incluidas las sub-asignaciones).
    const { error: assignmentError } = await supabase
      .from('tool_assignments')
      .update({
        returned_at: new Date().toISOString(),
        return_destination: 'warehouse',
        return_condition: 'lost',
        notes: reason || null
      })
      .eq('tool_id', tool_id)
      .is('returned_at', null);
    if (assignmentError) return { data: updated, error: assignmentError };

    // 3) Avisar a los administradores.
    //
    // El aviso sigue siendo best-effort —una pérdida ya registrada no se
    // deshace porque el correo interno no salga— pero YA NO ES MUDO. Antes
    // esto terminaba en `console.warn`, y como `createNotification` abortaba
    // por el RETURNING (ver el comentario en notificationService.js), el
    // diálogo prometía "Notifica al administrador" y en prod había CERO filas
    // con `notification_type='tool_lost_reported'` (medido 2026-07-30).
    // Ahora el fallo vuelve como `warning` en el resultado.
    let warning = null;
    try {
      const { data: admins, error: adminsError } = await supabase
        .from('profiles')
        .select('id')
        .eq('role', 'admin');

      if (adminsError) throw adminsError;

      const destinatarios = admins || [];
      const resultados = await Promise.all(destinatarios.map(async (a) => {
        const { error: notifyError } = await createNotification({
          user_id: a.id,
          notification_type: 'tool_lost_reported',
          title: 'Herramienta marcada como perdida',
          message: `"${tool.name}" (${tool.serial_number}) fue marcada como perdida. ${reason || ''}`.trim(),
          action_url: `/herramientas/${tool_id}`,
          related_type: 'tool',
          related_id: tool_id
        });
        return notifyError || null;
      }));

      const fallidas = resultados.filter(Boolean).length;
      if (destinatarios.length === 0) {
        warning = 'La pérdida quedó registrada, pero no hay ningún administrador a quien avisar.';
      } else if (fallidas > 0) {
        warning =
          `La pérdida quedó registrada, pero no se pudo notificar a ${fallidas} de ` +
          `${destinatarios.length} administrador(es). Avisales por otro medio.`;
      }
    } catch (notifyError) {
      warning =
        'La pérdida quedó registrada, pero falló la notificación a los administradores: ' +
        `${notifyError.message || notifyError}`;
    }

    if (warning) return { data: updated, error: null, warning };

    return { data: updated, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const getToolAssignmentHistory = async (toolId, { limit = 50, offset = 0 } = {}) => {
  try {
    const { data, error, count } = await supabase
      .from('tool_assignments')
      .select(`
        *,
        holder:profiles!holder_id(id, full_name),
        assigner:profiles!assigned_by(id, full_name),
        project:projects(id, project_number, title)
      `, { count: 'exact' })
      .eq('tool_id', toolId)
      .order('assigned_at', { ascending: false })
      .range(offset, offset + limit - 1);
    return { data, count, error };
  } catch (error) {
    return { data: null, count: 0, error };
  }
};

export const registerTool = async ({
  serial_number,
  name,
  category,
  category_id,
  location_id,
  brand,
  model,
  description,
  notes,
  image_url,
  imageFile,
  requires_approval
}) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    const { data: created, error: insertError } = await supabase
      .from('tools')
      .insert({
        serial_number: serial_number.trim(),
        name: name.trim(),
        category: category || null,
        category_id: category_id || null,
        location_id: location_id || null,
        brand: brand || null,
        model: model || null,
        description: description || null,
        notes: notes || null,
        // #568 — se persiste la RUTA del objeto, nunca una URL resuelta.
        image_url: canonicalImageValue(INVENTORY_IMAGES_BUCKET, image_url) || null,
        requires_approval: requires_approval === true,
        created_by: user?.id || null
      })
      .select()
      .single();
    if (insertError) return { data: null, error: insertError };

    if (imageFile && created?.id) {
      const upload = await uploadInventoryImage(imageFile, created.id);
      if (upload.error) {
        return { data: created, error: null, imageError: upload.error };
      }
      // #568 — `upload.path` (estable), NO `upload.url` (firmado a 1 hora).
      const { data: updated, error: updateError } = await supabase
        .from('tools')
        .update({ image_url: upload.path })
        .eq('id', created.id)
        .select()
        .single();
      if (updateError) return { data: created, error: null, imageError: updateError };
      // La ficha recién creada se pinta al toque: se devuelve la URL utilizable.
      if (updated?.image_url) updated.image_url = upload.url || updated.image_url;
      return { data: updated, error: null };
    }

    return { data: created, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const updateTool = async (toolId, updates) => {
  try {
    const allowed = [
      'name', 'category', 'category_id', 'location_id', 'brand', 'model',
      'description', 'notes', 'location', 'image_url',
      'requires_approval', 'serial_number'
    ];
    const payload = Object.fromEntries(
      Object.entries(updates).filter(([k]) => allowed.includes(k))
    );
    // #568 — si el llamador manda una URL, se reduce a la ruta antes de guardar.
    if (payload.image_url !== undefined) {
      payload.image_url = canonicalImageValue(INVENTORY_IMAGES_BUCKET, payload.image_url);
    }
    // serial_number dejó de ser inmutable (paridad con el carril MCP): mismo
    // trim que registerTool. La unicidad la sigue cortando el UNIQUE de la
    // base; `error.code === '23505'` llega tal cual al llamador.
    if (payload.serial_number !== undefined) {
      payload.serial_number = String(payload.serial_number).trim();
    }
    const { data, error } = await supabase
      .from('tools')
      .update(payload)
      .eq('id', toolId)
      .select()
      .single();
    return { data, error };
  } catch (error) {
    return { data: null, error };
  }
};

export const replaceToolImage = async (toolId, file, oldImageUrl = null) => {
  try {
    const upload = await uploadInventoryImage(file, toolId);
    if (upload.error) return { data: null, error: upload.error };

    // #568 — se persiste `upload.path`; `upload.url` es sólo para pintar ahora.
    const { data, error } = await supabase
      .from('tools')
      .update({ image_url: upload.path })
      .eq('id', toolId)
      .select()
      .single();
    if (error) return { data: null, error };

    if (oldImageUrl) {
      await deleteInventoryImage(oldImageUrl);
    }
    if (data?.image_url) data.image_url = upload.url || data.image_url;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const retireTool = async (toolId, { reason } = {}) => {
  try {
    const updates = { status: 'retired' };
    if (reason) {
      // Se AGREGA el motivo; antes se hacía `notes = reason`, que borraba las
      // notas internas de la herramienta al darla de baja (ver appendNote).
      const { data: current } = await supabase
        .from('tools').select('notes').eq('id', toolId).single();
      updates.notes = appendNote(current?.notes, `[BAJA ${todayStamp()}] ${reason}`);
    }
    const { data, error } = await supabase
      .from('tools')
      .update(updates)
      .eq('id', toolId)
      .select()
      .single();
    return { data, error };
  } catch (error) {
    return { data: null, error };
  }
};

export const subAssignTool = async ({ parent_assignment_id, holder_id, project_id, notes }) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { data: null, error: new Error('Not authenticated') };

    const { data: parent, error: parentError } = await supabase
      .from('tool_assignments')
      .select('id, tool_id, holder_id, returned_at, parent_assignment_id')
      .eq('id', parent_assignment_id)
      .single();
    if (parentError) return { data: null, error: parentError };
    if (parent.returned_at) {
      return { data: null, error: new Error('La asignación primaria ya fue devuelta.') };
    }
    if (parent.parent_assignment_id) {
      return { data: null, error: new Error('No se puede sub-asignar una sub-asignación (máximo 2 niveles).') };
    }
    if (parent.holder_id !== user.id) {
      return { data: null, error: new Error('Solo el holder primario puede sub-asignar esta herramienta.') };
    }

    const { data, error } = await supabase
      .from('tool_assignments')
      .insert({
        tool_id: parent.tool_id,
        holder_id,
        assigned_by: user.id,
        parent_assignment_id,
        project_id: project_id || null,
        notes: notes || null
      })
      .select()
      .single();
    return { data, error };
  } catch (error) {
    return { data: null, error };
  }
};

export const getToolHolder = async (toolId) => {
  try {
    const { data, error } = await supabase
      .from('tool_assignments')
      .select(`
        id, holder_id, assigned_at, parent_assignment_id, project_id,
        holder:profiles!holder_id(id, full_name),
        project:projects(id, project_number, title)
      `)
      .eq('tool_id', toolId)
      .is('returned_at', null)
      .is('parent_assignment_id', null)
      .maybeSingle();
    return { data, error };
  } catch (error) {
    return { data: null, error };
  }
};
